import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axios';
import { Text, IconButton, Grid, Box, Skeleton, SkeletonCircle } from '@chakra-ui/react';
import { useCart } from '../../context/CartContext';
import { SlBasket } from "react-icons/sl";
import { Link } from 'react-router-dom';
import TrackImage from './TrackImage';

const TopTracks = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchTopTracks();
    }, []);

    const fetchTopTracks = () => {
        setLoading(true);

        axiosInstance
            .get("/statistics/get_top_tracks")
            .then((res) => {
                setData(res.data.map(item => ({
                    track_id: item.track.track_id,
                    track_name: item.track.track_name,
                    artist_name: item.track.artist_name,
                    price_per_share: item.track.price_per_share,
                    publisher: item.track.publisher,
                    artist_id: item.track.artist_id || item.track.publisher?.id,
                    sales: item.sales,
                    image_url: item.track.image_url,
                    preview_url: item.track.preview_url,
                    track_url: item.track.track_url,
                })));
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const { addToCart } = useCart();

    // Create columns with 3 items each
    const createColumns = (items) => {
        const columns = [];
        for (let i = 0; i < items.length; i += 3) {
            columns.push(items.slice(i, i + 3));
        }
        return columns;
    };

    // Loading skeleton component
    const LoadingSkeleton = () => {
        const skeletonItems = Array(9).fill(0);
        const skeletonColumns = createColumns(skeletonItems);
        
        return (
            <Box
                display={{ base: "flex", md: "grid" }}
                gridTemplateColumns={{ md: "repeat(auto-fit, minmax(250px, 300px))", lg: "repeat(auto-fit, minmax(250px, 300px))", xl: "repeat(auto-fit, minmax(250px, 300px))" }}
                gap={{ base: 0, md: 4 }}
                overflowX={{ base: "auto", md: "visible" }}
                scrollSnapType={{ base: "x mandatory", md: "none" }}
                p={{ base: 0, md: 3 }}
                px={{ base: 0, md: 2 }}
                py={{ base: 2, md: 3 }}
                css={{
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                }}
            >
                {skeletonColumns.map((column, columnIndex) => (
                    <Box
                        key={columnIndex}
                        scrollSnapAlign={{ base: "start", md: "none" }}
                        display="flex"
                        flexDirection="column"
                        gap={3}
                        bg="transparent"
                        borderRadius="md"
                        p={3}
                        minW={{ base: "100vw", md: "250px" }}
                        maxW={{ base: "100vw", md: "none" }}
                        flex={{ base: "0 0 auto", md: "none" }}
                        px={{ base: 4, md: 0 }}
                    >
                        {column.map((_, itemIndex) => (
                            <Grid
                                key={itemIndex}
                                templateColumns="auto 1fr auto"
                                templateRows="1fr 1fr 1fr"
                                gap={2}
                                alignItems="center"
                                p={2}
                                bg="secondary.100"
                                borderRadius="md"
                                boxShadow="lg"
                                minH="70px"
                                w="100%"
                            >
                                <SkeletonCircle size="16" gridColumn="1/2" gridRow="1/4" alignSelf="center" />
                                <Box gridColumn="2/3" gridRow="1/4" justifySelf="flex-start">
                                    <Skeleton height="16px" width="120px" mb={1} />
                                    <Skeleton height="14px" width="80px" mb={1} />
                                    <Skeleton height="12px" width="40px" />
                                </Box>
                                <Skeleton height="32px" width="32px" gridColumn="3/4" gridRow="1/4" alignSelf="center" borderRadius="md" />
                            </Grid>
                        ))}
                    </Box>
                ))}
            </Box>
        );
    };

    if (loading) {
        return <LoadingSkeleton />;
    }

    if (data.length === 0) {
        return (
            <Box p={8} textAlign="center">
                <Text color="gray.500" fontSize="sm">
                    No top tracks available yet
                </Text>
            </Box>
        );
    }

    const columns = createColumns(data);

    return (
        <Box
            display={{ base: "flex", md: "grid" }}
            gridTemplateColumns={{ md: "repeat(auto-fit, minmax(250px, 300px))", lg: "repeat(auto-fit, minmax(250px, 300px))", xl: "repeat(auto-fit, minmax(250px, 300px))" }}
            gap={{ base: 0, md: 3 }}
            overflowX={{ base: "auto", md: "visible" }}
            scrollSnapType={{ base: "x mandatory", md: "none" }}

            py={{ base: 2, md: 3 }}
            css={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
            }}
        >
            {columns.map((column, columnIndex) => (
                <Box
                    key={columnIndex}
                    scrollSnapAlign={{ base: "start", md: "none" }}
                    display="flex"
                    flexDirection="column"
                    gap={3}
                    bg="transparent"
                    borderRadius="md"
                    p={3}
                    minW={{ base: "100%", md: "250px" }}
                    maxW={{ base: "100%" }}
                    flex={{ base: "0 0 auto", md: "none" }}
          
                >
                    {column.map((item) => (
                        <Grid
                            key={item.track_id}
                            templateColumns="auto 1fr auto"
                            templateRows="1fr 1fr 1fr"
                            gap={2}
                            alignItems="center"
                            p={2}
                            bg="secondary.100"
                            borderRadius="md"
                            boxShadow="lg"
                            _hover={{ transform: "translateY(-2px)", boxShadow: "xl" }}
                            transition="all 0.3s ease"
                            minH="70px"
                            w="100%"
                        >
                            <TrackImage item={item} />
                            <Grid gridColumn="2/3" gridRow="1/4" justifySelf="flex-start">
                                <Text 
                                    as={Link} 
                                    to={`/track/${item.track_id}`} 
                                    fontSize="sm" 
                                    color="primary.100" 
                                    noOfLines={1} 
                                    _hover={{ color: "dark.900" }}
                                >
                                    {item.track_name}
                                </Text>
                                <Text 
                                    as={Link} 
                                    to={`/artist/${item.publisher?.id || item.artist_id || '#'}`} 
                                    fontSize="sm" 
                                    noOfLines={1} 
                                    _hover={{ color: "dark.900" }}
                                >
                                    {item.artist_name}
                                </Text>
                                <Text fontSize="md">${item.price_per_share}</Text>
                            </Grid>
                            <IconButton
                                gridColumn="3/4"
                                gridRow="1/4"
                                icon={<SlBasket />}
                                onClick={() => addToCart(item)}
                                variant="ghost"
                                color="dark.900"
                                size="sm"
                                alignSelf="center"
                            />
                        </Grid>
                    ))}
                </Box>
            ))}
        </Box>
    );
};

export default TopTracks;